import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { profileData } from '../mock';
import { useToast } from '../hooks/use-toast';
import './Contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Message Sent!",
          description: data.message || "Thank you for reaching out. We'll get back to you within 24 hours.",
        });
        
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            organization: '',
            subject: '',
            message: ''
          });
        }, 3000);
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please check your internet connection and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="contact-header">
          <span className="section-label">Contact</span>
          <h2 className="section-title">Let's Discuss Your Strategic Needs</h2>
          <p className="section-description">
            Ready to navigate complex policy landscapes? Schedule a confidential consultation.
          </p>
        </div>

        <div className="contact-content">
          {/* Contact Info */}
          <div className="contact-info">
            <Card className="info-card">
              <h3 className="info-title">Get in Touch</h3>
              <p className="info-description">
                Whether you're facing regulatory challenges, seeking strategic counsel, or need expert analysis, I'm here to help.
              </p>

              <div className="contact-details">
                <div className="detail-item">
                  <div className="detail-icon">
                    <Mail size={24} />
                  </div>
                  <div className="detail-content">
                    <h4>Email</h4>
                    <a href={`mailto:${profileData.email}`}>{profileData.email}</a>
                  </div>
                </div>

                {profileData.phone && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <Phone size={24} />
                    </div>
                    <div className="detail-content">
                      <h4>Phone</h4>
                      <a href={`tel:${profileData.phone}`}>{profileData.phone}</a>
                    </div>
                  </div>
                )}

                <div className="detail-item">
                  <div className="detail-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="detail-content">
                    <h4>Location</h4>
                    <p>{profileData.location}</p>
                  </div>
                </div>
              </div>

              <div className="availability-note">
                <CheckCircle size={18} />
                <span>Available for consultations worldwide</span>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <Card className="form-card">
              {isSubmitted ? (
                <div className="success-message">
                  <CheckCircle size={64} className="success-icon" />
                  <h3>Message Sent Successfully!</h3>
                  <p>Thank you for reaching out. I'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="organization">Organization</label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="Your Company"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">Subject *</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="How can I help?"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Please describe your needs and objectives..."
                    ></textarea>
                  </div>

                  <Button type="submit" className="submit-button">
                    Send Message <Send size={18} />
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
